<template lang="pug">
q-dialog(:value="true", :persistent="submitting", @hide="closeForm")
  q-card.issue-import-from-friend
    form(@submit.prevent="submit")
      .issue-import-from-friend__header.form-card-title
        inline-svg.form-card-title-icon(:src="require('@/assets/icons/form/header/file_header.svg')")
        span {{ formData?.form_header?.add }}
        q-btn.issue-import-from-friend__close(
          flat,
          round,
          dense,
          type="button",
          :disable="submitting",
          :aria-label="i18n.messages[currentLocale].buttonCancelLabel",
          @click="closeForm"
        )
          inline-svg(:src="require('@/assets/icons/form/header/close.svg')")

      .issue-import-from-friend__loader(v-if="loading")
        q-spinner(color="primary", size="3em")

      template(v-else-if="formData")
        .issue-import-from-friend__fields
          .issue-import-from-friend__fields-row(v-for="(row, rowIndex) in rows", :key="rowIndex")
            .issue-import-from-friend__field(
              v-for="field in row",
              :key="field.name",
              :style="{ '--field-span': field.col }"
            )
              component(
                :is="fieldsComponents[field.type]",
                :label="field.label",
                :required="field.require ?? field.required",
                :initial-value="formValues[field.name]",
                :readonly="submitting || field.readonly",
                :multiple="field.multiple",
                :options="field.options || []",
                :options-params="field.options_params || {}",
                :full-options-path="store.state.paths[field.options_path] || field.options_path",
                :autogrow="field.type === 'text'",
                :form-loaded="!loading",
                :invalid="field.name in invalidFields",
                :error-message="invalidFields[field.name]",
                @change="value => updateField(field.name, value)"
              )

        .issue-import-from-friend__buttons
          q-btn.issue-import-from-friend__submit(
            flat,
            no-caps,
            type="submit",
            :label="i18n.messages[currentLocale].issueImportFromFriend.submit",
            :loading="submitting"
          )
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { Notify } from "quasar";
import backend from "@/api";
import fieldsComponents from "@/components/shared/general_components/fields";
import { handleError } from "@/services/handleErrors";
import { currentLocale, notifies } from "@/services/useLocales";
import { useStore } from "@/store";
import i18n from "@/plugins/vue-i18n";

const props = defineProps({
  facilityId: { type: Number, required: true },
  submitPath: { type: String, default: "" },
});

const emit = defineEmits(["close-form", "imported"]);
const store = useStore();
const loading = ref(true);
const submitting = ref(false);
const formData = ref(null);
const formValues = ref({});
const invalidFields = ref({});

const fields = computed(() => formData.value?.fields || []);
const rows = computed(() =>
  (formData.value?.schema || []).map(row =>
    row.map(item => ({ ...fields.value.find(field => field.name === item.name), col: item.col })),
  ),
);

const getFieldValue = value => {
  if (Array.isArray(value)) {
    return value.map(getFieldValue);
  }

  return value && typeof value === "object" && "value" in value ? value.value : value;
};

const updateField = (name, value) => {
  formValues.value = { ...formValues.value, [name]: getFieldValue(value) };
  const errors = { ...invalidFields.value };
  delete errors[name];
  invalidFields.value = errors;
};

const closeForm = () => {
  if (!submitting.value) {
    emit("close-form");
  }
};

const loadForm = async () => {
  try {
    const { data } = await backend.index("/api/v3/dynamic/issues/load_ff_form", {
      params: { facility_id: props.facilityId },
    });

    formData.value = data;
    formValues.value = Object.fromEntries(data.fields.map(field => [field.name, getFieldValue(field.value)]));
  } catch (error) {
    await handleError(error);
    closeForm();
  } finally {
    loading.value = false;
  }
};

const validate = () => {
  const errors = {};

  fields.value.forEach(field => {
    const value = formValues.value[field.name];
    const empty =
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim() === "") ||
      (Array.isArray(value) && value.length === 0);

    if ((field.require ?? field.required) && empty) {
      errors[field.name] = notifies.value.not_empty;
    }
  });

  invalidFields.value = errors;
  return Object.keys(errors).length === 0;
};

const submit = async () => {
  if (loading.value || submitting.value) {
    return;
  }

  if (!validate()) {
    Notify.create(notifies.value.fix_invalid_fields);
    return;
  }

  if (!props.submitPath) {
    return;
  }

  submitting.value = true;

  try {
    const params = { incident: formValues.value };
    const { data } = await backend.create(props.submitPath, null, params);
    emit("imported", data.data);
  } catch (error) {
    await handleError(error);
  } finally {
    submitting.value = false;
  }
};

onMounted(loadForm);
</script>

<style scoped lang="scss">
.issue-import-from-friend {
  width: 530px;
  max-width: calc(100vw - 32px);
  padding: 24px;
  border-radius: 15px;
  background: var(--modal-form-card-background);
  color: var(--modal-form-card-color);

  &__header {
    align-items: center;
    margin: 0;
    padding: 0;

    .form-card-title-icon {
      flex-shrink: 0;
    }
  }

  &__close {
    flex-shrink: 0;
    margin-left: auto;

    svg {
      width: 12px;
      height: 12px;
    }
  }

  &__loader {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 170px;
  }

  &__fields {
    display: flex;
    flex-direction: column;
    margin-top: 32px;
  }

  &__fields-row {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    column-gap: 10px;
  }

  &__field {
    grid-column: span var(--field-span);
    min-width: 0;

    :deep(.valid-error-message) {
      position: static;
      margin-bottom: 10px;
    }

    @media (max-width: 599px) {
      grid-column: 1 / -1;
    }
  }

  &__buttons {
    display: flex;
    justify-content: center;
    margin-top: 40px;
    margin-bottom: 14px;
  }

  &__submit {
    min-width: 200px;
    min-height: 40px;
    border-radius: 8px;
    background: var(--general-components-button-primary-background);
    color: var(--general-components-button-primary-color);
    font-weight: 600;
  }
}
</style>
